import cx from 'classnames'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import {
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useContext,
} from 'react'

import { type SanityLink } from '@data/sanity/queries/types/link'
import { LanguageContext } from '@lib/language-context'
import { getLinkPageUrl } from '@lib/routes'

import Button, {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  getButtonColor,
  getButtonIconAlignment,
  getButtonSize,
  getButtonVariant,
} from '@components/buttons/button'
import ButtonLink from '@components/buttons/button-link'

interface LinkProps {
  link: SanityLink
  onClick?: () => void
  children?: ReactNode
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  buttonColor?: ButtonColor
  nextLinkClassName?: string
}

type ExternalLinkProps = Pick<
  SanityLink,
  'url' | 'fontCase' | 'buttonStyle' | 'isButton'
> &
  Pick<
    LinkProps,
    'buttonVariant' | 'buttonSize' | 'buttonColor' | 'children'
  > & {
    id?: string
    ariaLabel?: string
    tabIndex?: number
    className?: string
  }

const ExternalLink = ({
  id,
  url,
  ariaLabel,
  tabIndex,
  buttonVariant,
  buttonSize,
  buttonColor,
  buttonStyle,
  isButton,
  fontCase,
  className,
  children,
}: ExternalLinkProps) => {
  const buttonStyles = isButton ? buttonStyle : undefined

  return (
    <ButtonLink
      id={id}
      href={url}
      target={url && !url.match('^mailto:') ? '_blank' : '_self'}
      aria-label={ariaLabel}
      tabIndex={tabIndex}
      rel="noopener noreferrer"
      variant={buttonVariant ?? getButtonVariant(buttonStyles?.variant)}
      size={buttonSize ?? getButtonSize(buttonStyles?.size)}
      color={buttonColor ?? getButtonColor(buttonStyles?.color)}
      icon={buttonStyles?.icon}
      iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
      className={cx(
        {
          btn: isButton,
          'w-full': buttonStyles?.isFullWidth,
        },
        fontCase ?? '',
        className
      )}
    >
      {children}
    </ButtonLink>
  )
}

const Link = ({
  link,
  children,
  tabIndex,
  onClick,
  className,
  buttonVariant,
  buttonSize,
  buttonColor,
  'aria-label': ariaLabel,
  nextLinkClassName,
}: LinkProps & HTMLAttributes<HTMLAnchorElement>) => {
  const { _id: id, url, isButton, fontCase, buttonStyle } = link
  const pageType = link.page?._type
  const pageSlug = link.page?.slug?.current

  // External link
  if (url) {
    return (
      <ExternalLink
        id={id}
        url={url}
        ariaLabel={ariaLabel}
        tabIndex={tabIndex}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        buttonColor={buttonColor}
        buttonStyle={buttonStyle}
        isButton={isButton}
        fontCase={fontCase}
        className={className}
      >
        {children}
      </ExternalLink>
    )
  }

  // Internal Page
  if (!pageType) {
    return null
  }

  const pageUrl = getLinkPageUrl(pageType, pageSlug)

  const getClickHandler = (event: MouseEvent<HTMLButtonElement>) => {
    onClick && onClick()
  }

  const beforeInputHandler = (event: KeyboardEvent<HTMLButtonElement>) => {
    onClick && onClick()
  }

  const buttonStyles = isButton ? buttonStyle : undefined

  return (
    <NextLink href={pageUrl} className={nextLinkClassName}>
      <Button
        id={id}
        role="link"
        tabIndex={tabIndex}
        onClick={getClickHandler}
        onBeforeInput={beforeInputHandler}
        aria-label={ariaLabel}
        variant={buttonVariant ?? getButtonVariant(buttonStyles?.variant)}
        size={buttonSize ?? getButtonSize(buttonStyles?.size)}
        color={buttonColor ?? getButtonColor(buttonStyles?.color)}
        icon={buttonStyles?.icon}
        iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
        className={cx(
          {
            btn: isButton,
            'w-full': buttonStyles?.isFullWidth,
          },
          fontCase ?? '',
          className
        )}
      >
        {children}
      </Button>
    </NextLink>
  )
}

export default Link
